import React from 'react';
import { Redirect } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import {
  BodyText,
  Button,
  DisplayText,
  IconLink,
  IllusLocation,
  LoadingPlaceholder,
  useResponsive,
} from '@garnish/core';
import { Navbar, NavbarBackLink } from '@garnish/navigation';
import { Modal, ModalDialogue } from '@garnish/overlays';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useCartHandoffMachine, useCartHandoffContent } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CartHandoffModal = (props: Props) => {
  const { isLiquidGlassEnabled } = props;

  const isFeatureEnabled = useFeatureFlag('CELS-5511-cart-handoff-enabled');

  if (!isFeatureEnabled) return <Redirect href="/" />;

  return <CartHandoffModalEnabled isLiquidGlassEnabled={isLiquidGlassEnabled} />;
};

const CartHandoffModalEnabled = (props: Props) => {
  const { isLiquidGlassEnabled = false } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();

  // ───────────────────────────────────────────────────────────────────────────

  const {
    conflicts,
    isDismissed,
    isInActiveBagWarning,
    isInConflictsWarning,
    isNothingAvailable,
    isHandingOffCart,
    triggerHandoff,
    dismissHandoff,
  } = useCartHandoffMachine();

  const { title, body, cta } = useCartHandoffContent({
    isDismissed,
    isHandingOffCart,
    isNothingAvailable,
    isInConflictsWarning,
    isInActiveBagWarning,
    conflicts,
  });

  // ─── Derived ───────────────────────────────────────────────────────────────

  const canDismiss = !isHandingOffCart;
  const shouldRenderCloseButton = canDismiss && !currentBreakpoint.isXS;
  const shouldRenderBackButton = canDismiss && currentBreakpoint.isXS;
  const shouldAddAndroidBottomSpacing = Platform.OS === 'android' && currentBreakpoint.isXS;

  // ─── Styles ────────────────────────────────────────────────────────────────

  // NOTE: The bottom inset sits inside the scroll content, so the scroll view
  //       does not clip the back button shadow above the home indicator.
  const footerContainerStyles = useStyle(() => {
    if (!currentBreakpoint.isXS) {
      return;
    }

    return { paddingBottom: safeAreaBottom };
  }, [currentBreakpoint.isXS, safeAreaBottom]);
  const footerStyles = [
    styles.footer,
    shouldAddAndroidBottomSpacing ? styles.footerAndroidXS : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      size="medium"
      imageSrc={IllusLocation}
      visible={!isDismissed}
      accessibilityLabel={formatMessage(messages.a11yLabel)}
      withSafeAreaView={false}
      withBottomPadding={!currentBreakpoint.isXS}
    >
      <View style={styles.content}>
        <Modal.Row style={styles.titleContainer}>
          <DisplayText sizeMatch={['24']}>{title}</DisplayText>

          {shouldRenderCloseButton ? (
            <IconLink
              name="IconClose"
              palette="muted"
              width={32}
              height={32}
              iconSize={20}
              aria-label={formatMessage(messages.close)}
              onPress={dismissHandoff}
            />
          ) : null}
        </Modal.Row>

        <Modal.Row>
          {body ? (
            <BodyText sizeMatch={['16']}>{body}</BodyText>
          ) : (
            <LoadingPlaceholder rowHeight={48} palette="cream" />
          )}
        </Modal.Row>

        <View style={footerContainerStyles}>
          <Modal.Footer withoutTopBorder style={footerStyles}>
            {shouldRenderBackButton ? (
              <Navbar.LinksGroup isLiquidGlassEnabled={isLiquidGlassEnabled}>
                <NavbarBackLink
                  accessibilityLabel={formatMessage(messages.back)}
                  onPress={dismissHandoff}
                />
              </Navbar.LinksGroup>
            ) : null}

            <View style={styles.ctaContainer}>
              <Button
                palette="primary"
                size="large"
                accessibilityRole="button"
                aria-label={cta}
                isLoading={isHandingOffCart}
                onPress={isNothingAvailable ? dismissHandoff : triggerHandoff}
              >
                {cta}
              </Button>
            </View>
          </Modal.Footer>
        </View>
      </View>
    </ModalDialogue>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Cart Handoff Dialogue',
    description: 'Cart Handoff | A11y Label',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
  back: {
    defaultMessage: 'Go back',
    description: 'Cart Handoff | Back button a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 1,
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  footerAndroidXS: {
    paddingBottom: theme.spacing['4'],
  },
  ctaContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isLiquidGlassEnabled?: boolean;
};
